import { LogOutIcon } from "@/components/Icons/LogOutIcon";
import { QuestionIcon } from "@/components/Icons/QuestionIcon";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuthActions, useAuthState } from "@/store/auth";
import { getUserAttribute, getUserName } from "@/utils/workos";
import { getUserPicture } from "@/utils/user";
import { DashboardIcon } from "@radix-ui/react-icons";
import { type ReactNode, useState } from "react";
import { useLocation } from "wouter";
import env from "@/env";
import { useExplorerOnlyMode } from "@/hooks/useExplorerOnlyMode";
import { explorerPrefixedPath } from "@/utils";
import { getCloudDashboardUrl } from "@/utils/tool";
import {
  UserDashboardMenuItemsContent
} from "@/components/Common/UserDashboardMenuItems/UserDashboardMenuItems";
import { useUserDashboardMenuVisibility } from "@/hooks/useUserDashboardMenuVisibility";

const menuButtonClass =
  "subtext-small flex cursor-pointer flex-row items-center rounded px-4 py-[10px] text-gray-dark-1000 transition-colors hover:bg-container hover:text-foreground dark:hover:bg-gray-dark-1100";

type UserMenuButtonProps = {
  icon: ReactNode;
  children: ReactNode;
  onClick: () => void;
};

const UserMenuButton = ({ icon, children, onClick }: UserMenuButtonProps) => (
  <div
    onClick={onClick}
    className={menuButtonClass}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => e.key === "Enter" && onClick()}
  >
    {icon}
    {children}
  </div>
);

type UserButtonProps = {
  onOpenSupport?: () => void;
};

export const UserButton = ({ onOpenSupport }: UserButtonProps) => {
  const { logout } = useAuthActions();
  const profile = useAuthState((state) => state.profile);
  const [, setLocation] = useLocation();
  const explorerOnlyMode = useExplorerOnlyMode();
  const [isOpen, setIsOpen] = useState(false);
  const visibility = useUserDashboardMenuVisibility(isOpen);
  const { hasWorkers, hasStaking } = visibility;
  const showProfile = !(env.EXPLORER_COMBINED && (hasWorkers || hasStaking));
  const displayName = getUserName(profile);
  const displayEmail = getUserAttribute(profile, "email");

  const handleLogout = async () => {
    await logout();
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
      }}
    >
      <PopoverTrigger
        data-testid={"UserButton_trigger"}
        className="subtext-small flex flex-row items-center gap-2 text-gray focus:outline-none"
      >
        <div className="flex h-[32px] w-[32px] flex-col items-center justify-center overflow-hidden rounded-full bg-gray-light-200 text-2xs uppercase text-black">
          <img src={getUserPicture(profile)} alt="" className={"h-full w-full object-cover"} />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="relative z-[999999] mt-2 flex w-[290px] rounded-[16px] border-0 p-0"
        align="end"
      >
        <div className="flex w-[290px] flex-auto flex-col gap-3 overflow-hidden rounded-[16px] bg-white p-4 dark:border dark:border-gray-dark-200 dark:bg-black">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center">
              <img
                src={getUserPicture(profile)}
                alt=""
                className={"mr-3 h-[32px] w-[32px] rounded-full object-cover"}
              />
              <div className="flex-1 truncate">
                <div className="subtext-small truncate text-black dark:text-foreground">
                  {typeof displayName === "string" && displayName.length > 0
                    ? displayName
                    : "Account"}
                </div>
                <div className="subtext-x-small text-gray-dark-1000">
                  {typeof displayEmail === "string" ? displayEmail : ""}
                </div>
              </div>
            </div>

            <div className="m-2 h-[1px] bg-gray-dark-900"></div>

            {showProfile && (
              <UserMenuButton
                icon={<DashboardIcon className="mr-2 h-[18px] w-[18px]" />}
                onClick={() => {
                  if (explorerOnlyMode) {
                    window.open(getCloudDashboardUrl(), "_blank", "noopener,noreferrer");
                  } else {
                    setLocation(explorerPrefixedPath("/id/account/overview"));
                  }
                }}
              >
                Profile
              </UserMenuButton>
            )}

            <UserDashboardMenuItemsContent variant="popover" visibility={visibility} />

            <UserMenuButton
              icon={<QuestionIcon className="mr-2 h-[18px] w-[18px]" />}
              onClick={() => onOpenSupport?.()}
            >
              Open Support Ticket
            </UserMenuButton>

            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-red-light-800 hover:text-red-light-800"
            >
              <LogOutIcon className="mr-1 h-[18px]" />
              <span>Sign Out</span>
              <div className="flex-1"></div>
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
